import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, registerAuthFailureHandler } from "../lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(undefined); // undefined=loading, null=unauth
  const [profile, setProfile] = useState(null);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data.user);
      setProfile(data.profile);
    } catch {
      // 401 on /auth/me means not logged in (or the refresh interceptor gave
      // up). Either way we render an unauthenticated UI.
      setUser(null);
      setProfile(null);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  // Wire the global silent-refresh failure handler. When axios can't
  // resurrect a session via /auth/refresh, clear local state so ProtectedRoute
  // bounces the user to /login. Memoised with useCallback so the same
  // reference is registered for the lifetime of the provider.
  const onAuthFailure = useCallback(() => {
    setUser(null);
    setProfile(null);
  }, []);
  useEffect(() => { registerAuthFailureHandler(onAuthFailure); }, [onAuthFailure]);

  const login = async (email, password) => {
    // Server sets BOTH the httpOnly `token` access cookie AND the
    // `refresh_token` cookie. We never see the JWT in JS.
    // ...unless the user has 2FA enabled — then the response is
    // {requires_2fa: true, challenge_token} and we return that to the
    // caller without flipping the auth state.
    const { data } = await api.post("/auth/login", { email, password });
    if (data?.requires_2fa) {
      return data;  // {requires_2fa: true, challenge_token}
    }
    await refresh();
    return data;
  };

  const login2fa = async (challenge_token, code) => {
    const { data } = await api.post("/auth/2fa/login", { challenge_token, code });
    await refresh();
    return data;
  };

  const recoveryLogin = async (challenge_token, recovery_code) => {
    const { data } = await api.post("/auth/2fa/recovery-login", { challenge_token, recovery_code });
    await refresh();
    return data;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); }
    catch { /* server logout failure shouldn't block local cleanup */ }
    setUser(null);
    setProfile(null);
  };

  return (
    <AuthContext.Provider value={{ user, profile, setUser, setProfile, login, login2fa, recoveryLogin, logout, refresh }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
